import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useBookingState } from "../../hooks/useBookingState";
import { toNumberString } from "../../utils/common";
import { ProdAttrUtils } from "../../utils/productAttributeUtils";
import { SelectTagArea } from "../detail/SelectTagArea";
import { AnimatePresence, motion } from "framer-motion";

export const TimeSlotSelection: React.FC<{
  calendarConfig?: TCalendarConfig[];
  activeTimeSlots?: any[];
  onTimeSlotChange: (timeSlotValue: any) => void;
  destDate?: string;
  destTimeslot?: string;
  productId?: number;
  countPerson?: number;
  loading?: boolean;
  mode: TProdAttrPickerMode;
}> = ({
  activeTimeSlots,
  onTimeSlotChange,
  calendarConfig,
  productId,
  destDate,
  mode,
  countPerson,
  loading,
  destTimeslot,
}) => {
  const { t } = useTranslation();
  const bookingState = useBookingState({
    mode,
  });
  const bookingStateRoot = useBookingState({
    mode: "FORM",
  });

  function formatTime(value?: string): string {
    if (!value) return "";
    const str = value.toString().padStart(4, "0");
    return str.slice(0, 2) + ":" + str.slice(2);
  }

  const calendarConfigModel = useMemo(() => {
    if (calendarConfig?.length) {
      const result = calendarConfig?.reduce(
        (acc, { date, ...rest }) => {
          acc[date] = rest;
          return acc;
        },
        {} as Record<string, Omit<(typeof calendarConfig)[number], "date">>,
      );
      return result;
    }
    return {};
  }, [calendarConfig]);

  const availableTime = useMemo(() => {
    return calendarConfigModel?.[
      dayjs(destDate, "YYYYMMDD").format("YYYY-MM-DD")
    ];
  }, [calendarConfigModel, destDate]);

  const destTimeslotFormat = formatTime(destTimeslot);

  const slotRemaining =
    availableTime?.availableGuest?.[destTimeslotFormat] || 0;

  const timeSlots = useMemo(() => {
    if (!calendarConfigModel || !destDate) return [];

    return availableTime?.timeSlots
      ?.map((slot) => {
        const maxNumberOfGuests = availableTime.availableGuest?.[slot];
        const time = +toNumberString(slot);

        return {
          displayName: slot,
          value: (time + "").padStart(4, "0"),
          maxNumberOfGuests,
        };
      })
      .sort((a: any, b: any) => +a.value - +b.value);
  }, [calendarConfigModel, destDate]);

  let selectedTimeSlot:
    | {
        displayName: string;
        value: string;
        maxNumberOfGuests?: number;
      }
    | undefined = undefined;
  if (activeTimeSlots && activeTimeSlots?.length > 0) {
    selectedTimeSlot = timeSlots?.find(
      (slot: any) => slot.value === activeTimeSlots[0],
    );
  }

  // const maxNumberOfGuests = calendarConfig?.defaultMaxNumberOfGuests;

  // const { slotRemaining, slotRemainingMap } = useSlotRemaining({
  //   destDate,
  //   productId,
  //   // timeSlot: selectedTimeSlot?.value,
  //   maxNumberOfGuests,
  // });
  const isPass = bookingStateRoot.product?.productType === "PASS";
  const count = isPass
    ? ProdAttrUtils.PASS.findPeopleQty(bookingStateRoot.bookingAttributes)
    : ProdAttrUtils.countPerson(bookingState);

  const [message, setMessage] = useState<string | null>(null);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const handleShowMessage = () => {
    if (!availableTime || !destDate || selectedTimeSlot?.value) return null;
    const minAvailable = Math.min(
      ...(Object.values(availableTime?.availableGuest || {}) || []),
    );
    if (minAvailable < count) {
      showToast(
        t("Some time slots do not have enough seats for the selected quantity"),
      );
    }
  };

  const showToast = (msg: string) => {
    setMessage(msg);

    // Nếu đã có timer trước đó thì clear đi
    if (timer) clearTimeout(timer);

    // Set timer mới 2s
    const newTimer = setTimeout(() => {
      setMessage(null);
    }, 2000);

    setTimer(newTimer);
  };

  useEffect(() => {
    handleShowMessage();
  }, [count, destDate]);

  if (!timeSlots?.length) {
    // if (loading) return <div>{t("Checking available time slots")}</div>;
    if (loading)
      return (
        <div>
          <Trans
            i18nKey={t(
              "Checking available ticket dates. Please do not refresh or go back",
            )}
          />
        </div>
      );
    return destDate ? (
      <div>{t("No time slots available")}</div>
    ) : (
      <div>{t("Please select date first")}</div>
    );
  }
  // const isShowText = (slotRemaining?: number) => {
  //   // if (!maxNumberOfGuests) return false;
  //   if (
  //     typeof slotRemaining !== "number"
  //       ? false
  //       : slotRemaining !== -1 && slotRemaining <= 5
  //   ) {
  //     return true;
  //   }
  //   return false;
  // };

  const notice = (): string => {
    if (slotRemaining < count)
      return t("There arent enough seats left", {
        num: slotRemaining,
      });
    if (slotRemaining <= 5)
      return t("Only 1 spot left", {
        num: slotRemaining,
      });

    return "";
  };

  // const isDisabled = (slotRemaining?: number) => {
  //   if (!maxNumberOfGuests) return false;
  //   if (
  //     typeof slotRemaining !== "number"
  //       ? false
  //       : slotRemaining !== -1 && slotRemaining - count < 0
  //   ) {
  //     return true;
  //   }
  //   if (typeof maxNumberOfGuests === "number") {
  //     return count > maxNumberOfGuests;
  //   }
  //   return false;
  // };

  return (
    <div className="time-slot-selection">
      <SelectTagArea
        containerClassName="grid-cols-3"
        options={timeSlots.map((slot: any) => {
          return {
            ...slot,
            disabled:
              !slot?.maxNumberOfGuests ||
              slot?.maxNumberOfGuests === 0 ||
              (slot?.maxNumberOfGuests || 0) < (countPerson || 0),
          };
        })}
        defaultSelected={activeTimeSlots}
        onSelect={(options) => {
          onTimeSlotChange(options[0]?.value);
        }}
        title={t("Select Time")}
      />
      {!!selectedTimeSlot?.value && (
        <>
          <div className="text-sm text-[tomato]">{notice()}</div>
          {slotRemaining < count && (
            <style>
              {` 
            .cart--btn {
              opacity: 0.5;
              pointer-events: none;
            }
            .popup-apply--btn {
              background-color: #DCDCDC;
              color: white;
            }
          `}
            </style>
          )}
        </>
      )}
      <AnimatePresence>
        {message && (
          <motion.div
            key="toast"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-[100px] left-1/2 z-50 w-full max-w-[calc(100dvw-32px)] -translate-x-1/2 rounded-md bg-gray-600 px-6 py-3 text-center text-[13px] text-white shadow-lg sm:bottom-12 sm:w-max sm:text-base"
          >
            {message}
          </motion.div>
        )}
        1
      </AnimatePresence>
    </div>
  );
};
